import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { importApi } from '../../api/imports'
import { mappingsApi } from '../../api/mappings'
import type { IssueSeverity, MappingDefinition, ValidatedRow } from '../../api/types'
import { useAuth } from '../../auth/useAuth'
import { useCompanies } from '../../companies/useCompanies'
import { Alert } from '../../components/Alert'
import { BbIcon, type BbIconName } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { IMPORT_STEPS } from './steps'
import { ErrorState, LoadingState } from '../../components/States'
import { useImportWizard } from '../../import/ImportWizardContext'
import { formatCredits, formatInt } from '../../lib/format'
import { useOpenSettings } from '../settings/useOpenSettings'

/* Step 4, ported from the "Review Import Data" prototype.

   Two tabs over one validation report: the row preview, and the references the
   validator could not resolve against QuickBooks. Both are derived from
   POST /api/import/validate — nothing here is fetched separately.

   Import Settings modal has been removed — settings are now managed centrally at /app/settings. */

/** The validator's code for a reference name it could not match (ValidationIssue.UnknownReference). */
const UNKNOWN_REF = 'UNKNOWN_REF'

type Tab = 'rows' | 'refs'

function severityOf(row: ValidatedRow): 'error' | 'warning' | 'ok' {
  if (row.issues.some((issue) => issue.severity === 'Error')) return 'error'
  if (row.issues.length > 0) return 'warning'
  return 'ok'
}

/* The icons are aria-hidden, so each status carries its own pill text. */
const STATUS: Record<
  'error' | 'warning' | 'ok',
  { icon: BbIconName; iconCls: string; pill: string; label: string }
> = {
  ok: { icon: 'check-circle', iconCls: 'bb-status-ok', pill: 'bb-apill bb-apill-ok', label: 'Valid' },
  warning: { icon: 'warning', iconCls: 'bb-status-warn', pill: 'bb-apill bb-apill-warn', label: 'Warning' },
  error: { icon: 'x-circle', iconCls: 'bb-status-bad', pill: 'bb-apill bb-apill-err', label: 'Error' },
}

/** "Item" → "Items", "Class" → "Classes". Good enough for QBO's reference names. */
function plural(word: string): string {
  return /(?:s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`
}

/** One unresolved reference value and the rows that asked for it. */
interface MissingRef {
  value: string
  fieldLabel: string
  suggestion: string | null
  rows: Set<number>
}

export function ImportReviewPage() {
  const wizard = useImportWizard()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const { current } = useCompanies()
  const [validating, setValidating] = useState(false)
  const openSettings = useOpenSettings()
  const [error, setError] = useState<ApiError | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('rows')
  const [refType, setRefType] = useState('')

  const { upload, entity, entityType, columns, validation, setValidation, options } = wizard

  /* The mapping carries only the column layout and any per-template overrides
     (from a saved mapping the user picked). Workspace settings — autoCreate
     toggles, date format, tolerance — are read by the server from the tenant's
     settings store, not from this bag. Sending them here caused a race: the
     reload after Apply had not landed yet, so the stale front-end copy
     overwrote the fresh server-side values and the settings appeared unsaved. */
  const mapping = useMemo<MappingDefinition | null>(
    () =>
      entityType
        ? { entityType, columns, ...(options ? { options } : {}) }
        : null,
    [entityType, columns, options],
  )

  useEffect(() => {
    if (!upload || !mapping || validation) return
    const controller = new AbortController()
    // Validation is the side effect this step exists for; the flag has to rise
    // with it so the preview shows a spinner rather than an empty report.
    // oxlint-disable-next-line react/set-state-in-effect
    setValidating(true)
    setError(null)
    importApi
      .validate(upload.uploadId, mapping, wizard.sheet, controller.signal)
      .then(setValidation)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause)))
      })
      .finally(() => setValidating(false))
    return () => controller.abort()
  }, [upload, mapping, validation, setValidation])

  const submit = async () => {
    if (!upload || !mapping || !current) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      if (wizard.saveMappingAs.trim()) {
        // Best-effort: a naming clash must not block the import itself.
        try {
          await mappingsApi.create(wizard.saveMappingAs.trim(), mapping.entityType, mapping)
        } catch {
          /* ignore */
        }
      }
      const { jobId } = await importApi.submit({
        uploadId: upload.uploadId,
        qboCompanyId: current.id,
        mapping,
        saveMappingAs: wizard.saveMappingAs.trim() || undefined,
        sheet: wizard.sheet,
      })
      void refreshUser()
      navigate(`/app/import/run/${jobId}`)
    } catch (cause: unknown) {
      setSubmitError(
        cause instanceof ApiError
          ? cause.isOutOfCredits
            ? `${cause.message} Top up from Billing & usage, then submit again.`
            : cause.message
          : 'Could not submit the job.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (!upload || !entity || !mapping) {
    return (
      <div className="bb bb-wizard">
        <div className="bb-empty-ref">
          <BbIcon name="folder" />
          <h3>Start with a file</h3>
          <p>
            Pick an import type and upload a file first.{' '}
            <Link to="/app/import">Back to step 1</Link>.
          </p>
        </div>
      </div>
    )
  }

  const mappedFields = columns.filter((column) => column.sourceColumn).map((column) => column.targetField)
  /* The wording the mapping step showed, so a field means the same thing on both
     screens. `label` is the export vocabulary the workbook templates match on,
     and reads as "RefNumber" or "Cust. Tax Code" — not what the user just chose. */
  const labelFor = (name: string) =>
    entity.fields.find((field) => field.name === name)?.displayLabel ?? name

  /* The reference types this entity can even have. FieldDef.referenceType is the
     only thing that says "this column is a QBO lookup", so it drives both the
     pill row and the grouping below. */
  const refTypes = [
    ...new Set(entity.fields.flatMap((field) => (field.referenceType ? [field.referenceType] : []))),
  ]
  const refTypeOfField = new Map<string, string>(
    entity.fields.flatMap((field) =>
      field.referenceType ? [[field.name, field.referenceType] as [string, string]] : [],
    ),
  )

  /* An UNKNOWN_REF issue on a reference field is a name the validator could not
     match in QuickBooks. Bucket those by reference type, then by the offending
     value, keeping the source rows so the count is rows and not issues. */
  const missing = new Map<string, Map<string, MissingRef>>()
  for (const row of validation?.rows ?? []) {
    for (const issue of row.issues) {
      if (issue.code !== UNKNOWN_REF || !issue.field) continue
      const type = refTypeOfField.get(issue.field)
      if (!type) continue
      const value = row.values[issue.field] ?? '(empty)'
      const byValue = missing.get(type) ?? new Map<string, MissingRef>()
      const entry = byValue.get(value) ?? {
        value,
        fieldLabel: labelFor(issue.field),
        suggestion: issue.suggestedValue,
        rows: new Set<number>(),
      }
      entry.suggestion ??= issue.suggestedValue
      entry.rows.add(row.sourceRow)
      byValue.set(value, entry)
      missing.set(type, byValue)
    }
  }

  const activeRefType = refType || refTypes[0] || ''
  const activeMissing = [...(missing.get(activeRefType)?.values() ?? [])].sort(
    (a, b) => b.rows.size - a.rows.size,
  )
  const activeLabel = activeRefType ? plural(activeRefType) : 'References'

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={IMPORT_STEPS} current={3} />

      <h2>Review Import Data</h2>
      <p className="bb-map-help">
        Please review the data below before confirming the import operation.
      </p>

      {error ? (
        <ErrorState error={error} onRetry={() => setValidation(null)} />
      ) : validating || !validation ? (
        <LoadingState label="Validating every row…" />
      ) : (
        <>
          {submitError ? (
            <Alert tone="err" title="Import not started">
              {submitError}
            </Alert>
          ) : null}

          <div className="bb-tabs" role="tablist" aria-label="Review sections">
            <button
              className={tab === 'rows' ? 'bb-tab bb-tab-green bb-tab-on' : 'bb-tab bb-tab-green'}
              type="button"
              role="tab"
              id="review-tab-rows"
              aria-controls="review-panel-rows"
              aria-selected={tab === 'rows'}
              onClick={() => setTab('rows')}
            >
              Records to Import
            </button>
            <button
              className={tab === 'refs' ? 'bb-tab bb-tab-green bb-tab-on' : 'bb-tab bb-tab-green'}
              type="button"
              role="tab"
              id="review-tab-refs"
              aria-controls="review-panel-refs"
              aria-selected={tab === 'refs'}
              onClick={() => setTab('refs')}
            >
              Import Missing References
            </button>
          </div>

          {tab === 'rows' ? (
            <div role="tabpanel" id="review-panel-rows" aria-labelledby="review-tab-rows">
              <table className="bb-rtable">
                <thead>
                  <tr>
                    <th className="bb-rtable-st">
                      <span className="sr-only">Status</span>
                    </th>
                    {mappedFields.map((name) => (
                      <th key={name}>{labelFor(name)}</th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {validation.rows.map((row) => {
                    const status = STATUS[severityOf(row)]
                    /* Which column each issue belongs to, so the cell that caused
                       a row to fail is the cell that looks wrong. Without this the
                       only mark was the row-level pill, and the reader had to
                       match a message against a column by eye — "Item 'Cabinets'
                       was not found" sitting beside a Description that happened to
                       hold the same word reads as though Description were the
                       problem. An issue names a field, and the columns are keyed
                       by the same field names, so the match is exact rather than
                       a guess. An Error outranks a Warning on the same field. */
                    const issueSeverity = new Map<string, IssueSeverity>()
                    for (const issue of row.issues) {
                      if (!issue.field) continue
                      const current = issueSeverity.get(issue.field)
                      if (current === 'Error') continue
                      issueSeverity.set(issue.field, issue.severity)
                    }

                    return (
                      <tr key={row.sourceRow}>
                        <td className="bb-rtable-st">
                          <span className={status.iconCls}>
                            <BbIcon name={status.icon} />
                          </span>
                        </td>
                        {mappedFields.map((name) => {
                          const severity = issueSeverity.get(name)
                          const cellCls =
                            severity === 'Error'
                              ? 'bb-cell-invalid'
                              : severity === 'Warning'
                                ? 'bb-cell-warning'
                                : undefined
                          return (
                            <td key={name} className={cellCls}>
                              {row.values[name] ?? '—'}
                            </td>
                          )
                        })}
                        <td>
                          <span className={status.pill}>
                            <i />
                            {status.label}
                          </span>
                          {row.issues.length > 0 ? (
                            <ul className="bb-issue-list">
                              {row.issues.map((issue, i) => (
                                <li key={i} className={issue.severity === 'Error' ? 'bb-issue-err' : 'bb-issue-warn'}>
                                  {issue.message}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="bb-rows-note">
                Showing {formatInt(validation.rows.length)} of {formatInt(validation.totalRows)} rows.
              </p>
            </div>
          ) : (
            <div role="tabpanel" id="review-panel-refs" aria-labelledby="review-tab-refs">
              {refTypes.length > 0 ? (
                <div className="bb-pills">
                  {refTypes.map((type) => (
                    <button
                      className={type === activeRefType ? 'bb-pill-btn bb-pill-btn-on' : 'bb-pill-btn'}
                      type="button"
                      key={type}
                      aria-pressed={type === activeRefType}
                      onClick={() => setRefType(type)}
                    >
                      {plural(type)}
                    </button>
                  ))}
                </div>
              ) : null}

              {activeMissing.length === 0 ? (
                <div className="bb-empty-ref">
                  <BbIcon name="package" />
                  <h3>No Missing {activeLabel} Found</h3>
                  <p>
                    Every {activeRefType ? activeRefType.toLowerCase() : 'reference'} in this file
                    already matches a record in QuickBooks, so there is nothing to create.
                  </p>
                </div>
              ) : (
                <>
                  <table className="bb-rtable">
                    <thead>
                      <tr>
                        <th>Missing {activeRefType}</th>
                        <th>Field</th>
                        <th className="bb-a-right">Rows Affected</th>
                        <th>Closest Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeMissing.map((entry) => (
                        <tr key={entry.value}>
                          <td>{entry.value}</td>
                          <td>{entry.fieldLabel}</td>
                          <td className="bb-a-right">{formatInt(entry.rows.size)}</td>
                          <td>{entry.suggestion ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="bb-rows-note">
                    {formatInt(activeMissing.length)} unresolved {activeLabel.toLowerCase()} across{' '}
                    {formatInt(validation.rows.length)} previewed rows.
                  </p>
                </>
              )}
            </div>
          )}

          <div className="bb-wizard-foot">
            <div className="bb-sum">
              <div>
                <div className="bb-sum-k">Ready to Import</div>
                <div className="bb-sum-v">{formatInt(validation.validRows)} rows</div>
              </div>
              {/* Only drawn when there is something to explain. A file whose
                  row count does not match what will be imported looks like a
                  bug unless the difference is named. */}
              {validation.skippedRows > 0 ? (
                <>
                  <span className="bb-sum-sep" />
                  <div>
                    <div className="bb-sum-k">Skipped</div>
                    <div className="bb-sum-v">{formatInt(validation.skippedRows)} rows</div>
                  </div>
                </>
              ) : null}
              <span className="bb-sum-sep" />
              <div>
                <div className="bb-sum-k">Cost</div>
                <div className="bb-sum-cost">
                  <BbIcon name="coins" />
                  {formatCredits(validation.creditEstimate)} Credits
                </div>
              </div>
            </div>
            <span className="bb-wizard-foot-r">
              <Link className="bb-btn bb-btn-quiet" to="/app/import/map">
                <BbIcon name="arrow-left" />
                Previous Step
              </Link>
              <button className="bb-btn bb-btn-quiet" type="button" onClick={openSettings}>
                <BbIcon name="tune" />
                Settings
              </button>
              <button
                className="bb-btn bb-btn-green"
                type="button"
                disabled={submitting || !current || validation.validRows === 0}
                onClick={submit}
              >
                <BbIcon name="play" />
                {submitting ? 'Starting…' : 'Start Import'}
              </button>
            </span>
          </div>
        </>
      )}

    </div>
  )
}
